import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Plus,
  Search,
  Train,
  Trash2,
} from 'lucide-react';
import {
  ApiError,
  api,
  canEdit,
  type AuthUser,
  type Player,
  type TrainAssignment,
  type TrainAssignmentBody,
  type TrainKind,
  type TrainStatsRow,
  type TrainVipKind,
} from '@/lib/api';
import { formatLocalHhmm, localToUtcIso } from '@/lib/serverTime';
import { PlayerFlagChip } from '@/components/PlayerFlagChip';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RANK_ORDER: Record<string, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay();
  const diff = (dow + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function weekDates(monday: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => isoDate(addDays(monday, i)));
}

type DayAssignments = { regular: TrainAssignment | null; megas: TrainAssignment[] };
const MAX_MEGAS_PER_DAY = 2;

export default function Trains({ user }: { user: AuthUser }) {
  const editable = canEdit(user.role);
  const today = useMemo(() => new Date(), []);
  const [anchorMonday, setAnchorMonday] = useState(() => mondayOf(today));

  const thisMonday = anchorMonday;
  const nextMonday = addDays(anchorMonday, 7);
  const rangeFrom = isoDate(thisMonday);
  const rangeTo = isoDate(addDays(nextMonday, 6));

  const trainsQuery = useQuery({
    queryKey: ['trains', rangeFrom, rangeTo],
    queryFn: () => api.listTrains({ from: rangeFrom, to: rangeTo }),
  });
  const statsQuery = useQuery({
    queryKey: ['train-stats'],
    queryFn: () => api.getTrainStats(),
  });
  const playersQuery = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });

  const weekNotesQuery = useQuery({
    queryKey: ['train-week-notes', rangeFrom, rangeTo],
    queryFn: () => api.listTrainWeekNotes({ from: rangeFrom, to: rangeTo }),
  });
  const notesByWeek = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of weekNotesQuery.data ?? []) map.set(n.weekStart, n.note);
    return map;
  }, [weekNotesQuery.data]);

  const byDate = useMemo(() => {
    const map = new Map<string, DayAssignments>();
    for (const t of trainsQuery.data ?? []) {
      const existing = map.get(t.date) ?? { regular: null, megas: [] };
      if (t.kind === 'mega_express') existing.megas.push(t);
      else existing.regular = t;
      map.set(t.date, existing);
    }
    for (const day of map.values()) {
      day.megas.sort((a, b) => {
        const ax = a.setAt ?? '';
        const bx = b.setAt ?? '';
        if (ax && bx) return ax.localeCompare(bx);
        if (ax) return -1;
        if (bx) return 1;
        return a.id - b.id;
      });
    }
    return map;
  }, [trainsQuery.data]);

  const sortedPlayers = useMemo(() => {
    const isRed = (p: Player) => {
      const flags = p.activeFlags ?? [];
      if (flags.some((f) => f.kind === 'red')) return true;
      return flags.filter((f) => f.kind === 'yellow').length >= 2;
    };
    const rows = (playersQuery.data ?? []).filter((p) => p.active && !isRed(p));
    const hasHint = (p: Player) =>
      (p.activeFlags ?? []).some((f) => f.kind === 'train_hint');
    return rows.sort((a, b) => {
      const ha = hasHint(a) ? 1 : 0;
      const hb = hasHint(b) ? 1 : 0;
      if (ha !== hb) return hb - ha;
      const r = (RANK_ORDER[b.rank] ?? 0) - (RANK_ORDER[a.rank] ?? 0);
      if (r) return r;
      return a.name.localeCompare(b.name);
    });
  }, [playersQuery.data]);

  const allPlayers = playersQuery.data ?? [];

  const todayISO = isoDate(today);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Train size={20} /> Train tracker
          </h1>
          <p className="text-sm text-slate-400">
            One regular conductor per day, plus an optional Mega-Express. Set
            time is your local time.
          </p>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <button
            onClick={() => setAnchorMonday((m) => addDays(m, -7))}
            className="rounded border border-slate-700 p-1.5 hover:bg-slate-800"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setAnchorMonday(mondayOf(new Date()))}
            className="rounded border border-slate-700 px-3 py-1 hover:bg-slate-800"
          >
            Today
          </button>
          <button
            onClick={() => setAnchorMonday((m) => addDays(m, 7))}
            className="rounded border border-slate-700 p-1.5 hover:bg-slate-800"
            aria-label="Next week"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WeekPanel
          title="This week"
          monday={thisMonday}
          byDate={byDate}
          note={notesByWeek.get(rangeFrom) ?? ''}
          players={sortedPlayers}
          allPlayers={allPlayers}
          editable={editable}
          today={todayISO}
        />
        <WeekPanel
          title="Next week"
          monday={nextMonday}
          byDate={byDate}
          note={notesByWeek.get(isoDate(nextMonday)) ?? ''}
          players={sortedPlayers}
          allPlayers={allPlayers}
          editable={editable}
          today={todayISO}
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          Leaderboard (all-time)
        </h2>
        <Leaderboard rows={statsQuery.data ?? []} />
      </section>

      <Style />
    </div>
  );
}

function WeekPanel({
  title,
  monday,
  byDate,
  note,
  players,
  allPlayers,
  editable,
  today,
}: {
  title: string;
  monday: Date;
  byDate: Map<string, DayAssignments>;
  note: string;
  players: Player[];
  allPlayers: Player[];
  editable: boolean;
  today: string;
}) {
  const dates = weekDates(monday);
  const rangeLabel = `${isoDate(monday).slice(5)} – ${isoDate(addDays(monday, 6)).slice(5)}`;
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
          {title}
        </h2>
        <span className="text-xs text-slate-500">{rangeLabel}</span>
      </div>
      <WeekNoteEditor
        weekStart={isoDate(monday)}
        initialNote={note}
        editable={editable}
      />
      <ul className="space-y-2">
        {dates.map((date, i) => (
          <DayBlock
            key={date}
            date={date}
            weekday={WEEKDAYS[i]}
            day={byDate.get(date) ?? { regular: null, megas: [] }}
            players={players}
            allPlayers={allPlayers}
            editable={editable}
            isToday={date === today}
            today={today}
          />
        ))}
      </ul>
    </section>
  );
}

function WeekNoteEditor({
  weekStart,
  initialNote,
  editable,
}: {
  weekStart: string;
  initialNote: string;
  editable: boolean;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initialNote);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setValue(initialNote);
  }, [initialNote, weekStart]);
  const save = useMutation({
    mutationFn: () => api.setTrainWeekNote(weekStart, value.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['train-week-notes'] });
      setEditing(false);
    },
  });
  if (!editing) {
    if (!initialNote && !editable) return null;
    return (
      <div className="mb-2 flex items-start gap-2 rounded border border-slate-800 bg-slate-950/40 px-2 py-1.5 text-xs">
        <div className="flex-1 whitespace-pre-wrap text-slate-300">
          {initialNote || (
            <span className="italic text-slate-600">No description yet.</span>
          )}
        </div>
        {editable && (
          <button
            onClick={() => setEditing(true)}
            className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 hover:bg-slate-800 hover:text-white"
          >
            Edit
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="mb-2 space-y-1">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        placeholder="Description for this week (visible publicly)"
        className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
        autoFocus
      />
      <div className="flex justify-end gap-1">
        <button
          onClick={() => {
            setValue(initialNote);
            setEditing(false);
          }}
          className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded bg-indigo-600 px-2 py-0.5 text-[10px] text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function DayBlock({
  date,
  weekday,
  day,
  players,
  allPlayers,
  editable,
  isToday,
  today,
}: {
  date: string;
  weekday: string;
  day: DayAssignments;
  players: Player[];
  allPlayers: Player[];
  editable: boolean;
  isToday: boolean;
  today: string;
}) {
  const qc = useQueryClient();
  const [addingMega, setAddingMega] = useState(false);
  const isPast = date < today;
  const [open, setOpen] = useState(!isPast);
  const createMega = useMutation({
    mutationFn: (playerId: number) =>
      api.createMegaExpressTrain({ date, playerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trains'] });
      qc.invalidateQueries({ queryKey: ['train-stats'] });
      setAddingMega(false);
    },
  });

  const regularLabel = day.regular?.player
    ? day.regular.player.displayName || day.regular.player.name
    : null;
  const summary = [
    regularLabel ?? 'no conductor',
    day.megas.length > 0 ? `${day.megas.length}× MX` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li
      className={`space-y-1.5 rounded border p-2 ${
        isToday
          ? 'border-indigo-600/70 bg-indigo-900/20'
          : 'border-slate-800 bg-slate-950/40'
      } ${isPast && !open ? 'opacity-70' : ''}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-2 text-left"
        aria-expanded={open}
      >
        <span className="text-slate-500">{open ? '▾' : '▸'}</span>
        <div className="text-sm font-medium">{weekday}</div>
        <div className="text-xs text-slate-500 tabular-nums">{date.slice(5)}</div>
        {!open && (
          <div className="ml-auto truncate text-xs text-slate-400">
            {summary}
          </div>
        )}
      </button>

      {!open ? null : (
      <>
      <TrainSlot
        date={date}
        kind="regular"
        assignment={day.regular}
        players={players}
        allPlayers={allPlayers}
        editable={editable}
      />

      {day.megas.map((mega) => (
        <TrainSlot
          key={mega.id}
          date={date}
          kind="mega_express"
          assignment={mega}
          players={players}
          allPlayers={allPlayers}
          editable={editable}
        />
      ))}

      {day.megas.length < MAX_MEGAS_PER_DAY && editable ? (
        addingMega ? (
          <div className="rounded border border-fuchsia-700/60 bg-fuchsia-900/10 p-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-300">
              Mega-Express
            </div>
            <div className="flex items-center gap-2">
              <PlayerPicker
                players={players}
                selectedId={null}
                selectedLabel={null}
                disabled={createMega.isPending}
                onSelect={(id) => createMega.mutate(id)}
              />
              <button
                onClick={() => setAddingMega(false)}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingMega(true)}
            className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-fuchsia-700/60 px-2 py-1 text-xs text-fuchsia-400 hover:bg-fuchsia-950/30"
          >
            <Plus size={12} /> Add Mega-Express
          </button>
        )
      ) : null}
      </>
      )}
    </li>
  );
}

function TrainSlot({
  date,
  kind,
  assignment,
  players,
  allPlayers,
  editable,
}: {
  date: string;
  kind: TrainKind;
  assignment: TrainAssignment | null;
  players: Player[];
  allPlayers: Player[];
  editable: boolean;
}) {
  const qc = useQueryClient();
  const [draftPlayerId, setDraftPlayerId] = useState<number | null>(
    assignment?.player.id ?? null,
  );
  const [notes, setNotes] = useState(assignment?.notes ?? '');
  const [setAtHhmm, setSetAtHhmm] = useState(
    assignment?.setAt ? formatLocalHhmm(assignment.setAt) : '',
  );
  const [scoreStr, setScoreStr] = useState(
    assignment?.score != null ? String(assignment.score) : '',
  );
  const [vipPlayerId, setVipPlayerId] = useState<number | null>(
    assignment?.vip?.player.id ?? null,
  );
  const [vipKind, setVipKind] = useState<TrainVipKind | null>(
    assignment?.vip?.kind ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraftPlayerId(assignment?.player.id ?? null);
    setNotes(assignment?.notes ?? '');
    setSetAtHhmm(assignment?.setAt ? formatLocalHhmm(assignment.setAt) : '');
    setScoreStr(assignment?.score != null ? String(assignment.score) : '');
    setVipPlayerId(assignment?.vip?.player.id ?? null);
    setVipKind(assignment?.vip?.kind ?? null);
  }, [assignment?.id, assignment?.player.id, assignment?.setAt, assignment?.score, assignment?.vip?.player.id, assignment?.vip?.kind, assignment?.notes]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['trains'] });
    qc.invalidateQueries({ queryKey: ['train-stats'] });
    qc.invalidateQueries({ queryKey: ['train-history'] });
  };

  const save = useMutation({
    mutationFn: (body: TrainAssignmentBody) =>
      assignment
        ? api.updateTrainAssignmentById(assignment.id, body)
        : api.setTrainAssignment(date, { ...body, kind }),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const clear = useMutation({
    mutationFn: () =>
      assignment
        ? api.clearTrainAssignmentById(assignment.id)
        : api.clearTrainAssignment(date, kind),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const isMega = kind === 'mega_express';
  const displayName =
    assignment && (assignment.player.displayName?.trim() || assignment.player.name);

  const draftSetAtIso = setAtHhmm ? localToUtcIso(date, setAtHhmm) : null;
  const draftScore = scoreStr.trim() === '' ? null : Number(scoreStr);
  const draftVipKind = vipPlayerId ? vipKind : null;
  const draftNotes = notes.trim() || null;

  const dirty = !!assignment && (
    draftPlayerId !== assignment.player.id ||
    draftSetAtIso !== (assignment.setAt ?? null) ||
    draftScore !== (assignment.score ?? null) ||
    vipPlayerId !== (assignment.vip?.player.id ?? null) ||
    draftVipKind !== (assignment.vip?.kind ?? null) ||
    draftNotes !== (assignment.notes ?? null)
  );

  const submit = () => {
    if (!assignment || draftPlayerId == null) return;
    save.mutate({
      playerId: draftPlayerId,
      notes: draftNotes,
      setAt: draftSetAtIso,
      score: draftScore,
      vipPlayerId,
      vipKind: draftVipKind,
    });
  };

  const revert = () => {
    setDraftPlayerId(assignment?.player.id ?? null);
    setNotes(assignment?.notes ?? '');
    setSetAtHhmm(assignment?.setAt ? formatLocalHhmm(assignment.setAt) : '');
    setScoreStr(assignment?.score != null ? String(assignment.score) : '');
    setVipPlayerId(assignment?.vip?.player.id ?? null);
    setVipKind(assignment?.vip?.kind ?? null);
    setError(null);
  };

  const showBody = !!assignment;

  return (
    <div
      className={`rounded border p-2 ${
        isMega
          ? 'border-fuchsia-700/60 bg-fuchsia-900/10'
          : 'border-slate-800 bg-slate-900/40'
      }`}
    >
      {isMega && (
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-300">
          Mega-Express
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {editable ? (
          <PlayerPicker
            players={players}
            selectedId={draftPlayerId}
            selectedLabel={(() => {
              if (!draftPlayerId) return null;
              const p = allPlayers.find((x) => x.id === draftPlayerId);
              if (p)
                return { name: p.displayName?.trim() || p.name, rank: p.rank };
              return assignment
                ? { name: displayName ?? '', rank: assignment.player.rank }
                : null;
            })()}
            disabled={save.isPending || clear.isPending}
            onSelect={(id) => {
              if (!assignment) {
                // Empty slot: create the row with only the player set (no
                // set_at → no push). Further edits go through Save.
                save.mutate({ playerId: id });
              } else {
                // Existing row: stage the change; commit via Save.
                setDraftPlayerId(id);
              }
            }}
          />
        ) : (
          <div className="flex-1 min-w-40 text-sm">
            {displayName ? (
              <>
                <span className="font-medium">{displayName}</span>{' '}
                <span className="text-xs text-slate-500">
                  ({assignment!.player.rank})
                </span>
              </>
            ) : (
              <span className="text-slate-500">—</span>
            )}
          </div>
        )}

        {editable && assignment && (
          <button
            type="button"
            onClick={() => clear.mutate()}
            disabled={clear.isPending}
            className="rounded border border-slate-700 p-1 text-slate-500 hover:bg-slate-800 hover:text-red-400"
            title="Clear"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {showBody && (
        <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <span className="w-16 shrink-0">Set (local)</span>
            <input
              type="time"
              value={setAtHhmm}
              disabled={!editable}
              onChange={(e) => setSetAtHhmm(e.target.value)}
              className="input flex-1"
            />
            <span className="w-24 shrink-0 tabular-nums text-slate-500">
              ETD{' '}
              {setAtHhmm
                ? (() => {
                    const iso = localToUtcIso(date, setAtHhmm);
                    const etdIso = new Date(new Date(iso).getTime() + 4 * 3600_000).toISOString();
                    return formatLocalHhmm(etdIso);
                  })()
                : '—'}
            </span>
          </label>

          <label className="flex items-center gap-1.5 text-xs text-slate-400">
            <span className="w-16 shrink-0">Score</span>
            <input
              type="number"
              min={0}
              max={999999}
              step={1}
              value={scoreStr}
              disabled={!editable}
              onChange={(e) => setScoreStr(e.target.value)}
              placeholder="e.g. 250000"
              className="input flex-1"
            />
          </label>

          <label className="flex items-center gap-1.5 text-xs text-slate-400 sm:col-span-2">
            <span className="w-16 shrink-0">VIP</span>
            <VipPicker
              players={allPlayers}
              selectedId={vipPlayerId}
              disabled={!editable}
              onSelect={(id) => {
                setVipPlayerId(id);
                if (id && !vipKind) setVipKind('guest');
                if (!id) setVipKind(null);
              }}
            />
            <select
              value={vipKind ?? ''}
              disabled={!editable || !vipPlayerId}
              onChange={(e) =>
                setVipKind((e.target.value || null) as TrainVipKind | null)
              }
              className="input"
            >
              <option value="">—</option>
              <option value="defender">Defender</option>
              <option value="guest">Guest</option>
            </select>
          </label>

          <input
            value={notes}
            disabled={!editable}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)…"
            className="input text-xs sm:col-span-2"
          />

          {editable && (
            <div className="flex items-center justify-end gap-2 sm:col-span-2">
              {dirty && (
                <button
                  type="button"
                  onClick={revert}
                  disabled={save.isPending}
                  className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-slate-800 disabled:opacity-50"
                >
                  Discard
                </button>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={!dirty || save.isPending}
                className={`rounded px-3 py-1 text-xs font-medium ${
                  isMega
                    ? 'bg-fuchsia-600 text-white hover:bg-fuchsia-500'
                    : 'bg-indigo-600 text-white hover:bg-indigo-500'
                } disabled:cursor-not-allowed disabled:opacity-40`}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      )}
      {error && <div className="mt-1 text-xs text-red-400">{error}</div>}
    </div>
  );
}

function VipPicker({
  players,
  selectedId,
  disabled,
  onSelect,
}: {
  players: Player[];
  selectedId: number | null;
  disabled: boolean;
  onSelect: (id: number | null) => void;
}) {
  const selected = players.find((p) => p.id === selectedId) ?? null;
  return (
    <div className="flex flex-1 items-center gap-1">
      <PlayerPicker
        players={players}
        selectedId={selectedId}
        selectedLabel={
          selected
            ? {
                name: selected.displayName?.trim() || selected.name,
                rank: selected.rank,
              }
            : null
        }
        disabled={disabled}
        onSelect={(id) => onSelect(id)}
      />
      {selectedId != null && !disabled && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className="rounded border border-slate-700 p-1 text-slate-500 hover:bg-slate-800 hover:text-red-400"
          title="Clear VIP"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

function Leaderboard({ rows }: { rows: TrainStatsRow[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const today = isoDate(new Date());

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">#</th>
            <th className="px-3 py-2 text-left">Player</th>
            <th className="px-3 py-2 text-left">Rank</th>
            <th className="px-3 py-2 text-left">Last train</th>
            <th className="px-3 py-2 text-right">Trains driven</th>
            <th className="w-8 px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-center text-slate-500">
                No assignments yet.
              </td>
            </tr>
          )}
          {rows.map((row, i) => {
            const isOpen = expanded === row.playerId;
            const daysAgo =
              row.lastDate &&
              Math.floor(
                (Date.parse(today) - Date.parse(row.lastDate)) /
                  (1000 * 60 * 60 * 24),
              );
            return (
              <FragmentRow key={row.playerId}>
                <tr
                  className="cursor-pointer hover:bg-slate-900/40"
                  onClick={() => setExpanded(isOpen ? null : row.playerId)}
                >
                  <td className="px-3 py-2 text-slate-500 tabular-nums">
                    {i + 1}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">
                      {row.displayName?.trim() || row.name}
                    </div>
                    {row.displayName && row.displayName !== row.name && (
                      <div className="text-xs text-slate-500">{row.name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-400">{row.rank}</td>
                  <td className="px-3 py-2 text-slate-300">
                    {row.lastDate ? (
                      <>
                        <span className="tabular-nums">{row.lastDate}</span>
                        {typeof daysAgo === 'number' && (
                          <span className="ml-2 text-xs text-slate-500">
                            {daysAgo === 0
                              ? 'today'
                              : daysAgo === 1
                                ? 'yesterday'
                                : `${daysAgo}d ago`}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {row.count}
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={6} className="bg-slate-950/60 px-3 py-3">
                      <HistoryPanel playerId={row.playerId} />
                    </td>
                  </tr>
                )}
              </FragmentRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function HistoryPanel({ playerId }: { playerId: number }) {
  const historyQuery = useQuery({
    queryKey: ['train-history', playerId],
    queryFn: () => api.getTrainHistory(playerId),
  });
  if (historyQuery.isLoading)
    return <div className="text-xs text-slate-500">Loading history…</div>;
  if (historyQuery.isError || !historyQuery.data)
    return <div className="text-xs text-red-400">Failed to load history.</div>;
  const rows = historyQuery.data;
  if (rows.length === 0)
    return <div className="text-xs text-slate-500">No trains yet.</div>;
  return (
    <div className="space-y-1">
      <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">
        History ({rows.length})
      </div>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((h) => (
          <li
            key={`${h.date}-${h.notes ?? ''}`}
            className="flex items-baseline gap-2 rounded border border-slate-800 bg-slate-900/50 px-2 py-1 text-xs"
          >
            <span className="tabular-nums text-slate-200">{h.date}</span>
            {h.notes && (
              <span className="truncate text-slate-400" title={h.notes}>
                {h.notes}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlayerPicker({
  players,
  selectedId,
  selectedLabel,
  disabled,
  onSelect,
}: {
  players: Player[];
  selectedId: number | null;
  selectedLabel: { name: string; rank: string } | null;
  disabled: boolean;
  onSelect: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => {
      const name = (p.displayName?.trim() || p.name).toLowerCase();
      const original = p.name.toLowerCase();
      return name.includes(q) || original.includes(q);
    });
  }, [players, query]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    setQuery('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (id: number) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative flex-1 min-w-40">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded border px-2 py-1 text-left text-sm ${
          selectedId
            ? 'border-slate-700 bg-slate-950 hover:border-slate-600'
            : 'border-dashed border-slate-700 bg-slate-950/60 text-slate-500 hover:border-slate-600'
        } disabled:opacity-50`}
      >
        {selectedLabel ? (
          <span className="truncate">
            <span className="font-medium">{selectedLabel.name}</span>{' '}
            <span className="text-xs text-slate-500">({selectedLabel.rank})</span>
          </span>
        ) : (
          <span>Assign…</span>
        )}
        <Search size={14} className="shrink-0 text-slate-500" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(filtered.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const p = filtered[highlight];
                if (p) pick(p.id);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder="Search player…"
            className="w-full border-b border-slate-800 bg-slate-950 px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No match.</li>
            )}
            {filtered.map((p, i) => {
              const label = p.displayName?.trim() || p.name;
              const isSel = p.id === selectedId;
              const isHi = i === highlight;
              const hint = (p.activeFlags ?? []).find((f) => f.kind === 'train_hint');
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(p.id)}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                      isHi ? 'bg-slate-800' : ''
                    } ${isSel ? 'text-indigo-300' : ''}`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      {hint && (
                        <span
                          title={hint.note ?? 'Suggested for a train'}
                          className="shrink-0"
                        >
                          <Train size={12} className="text-sky-300" />
                        </span>
                      )}
                      <span className="truncate">
                        {label}
                        {p.displayName && p.displayName !== p.name && (
                          <span className="ml-2 text-xs text-slate-500">
                            {p.name}
                          </span>
                        )}
                      </span>
                      <span
                        onClick={(e) => e.stopPropagation()}
                        className="ml-1 shrink-0"
                      >
                        <PlayerFlagChip
                          playerId={p.id}
                          flags={p.activeFlags ?? []}
                          size={11}
                        />
                      </span>
                    </span>
                    <span className="ml-2 shrink-0 text-xs text-slate-500">
                      {p.rank}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 13px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
      .input:disabled { opacity: 0.6; }
    `}</style>
  );
}
